import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  DialogContentText
} from '@mui/material';
import { Edit, Delete, Add, Home } from '@mui/icons-material';
import { Zone, Farm } from '../../types';
import { api } from '../../services/api';

const ZoneList: React.FC = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [farm, setFarm] = useState<Farm | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Zone | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState<{ name: string }>({ name: '' });
  const [search, setSearch] = useState('');
  const [formError, setFormError] = useState<{ name?: string }>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchFarmAndZones = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!farmId) {
        throw new Error('Farm ID is required');
      }
      
      const { data: farmData, error: farmError } = await api.farms.getById(farmId);
      const { data: zonesData, error: zonesError } = await api.zones.getAll(farmId);
      
      if (farmError || zonesError) {
        const farmErrorMsg = typeof farmError === 'string'
          ? farmError
          : (farmError && typeof (farmError as any).message === 'string' ? (farmError as any).message : '');
        const zonesErrorMsg = typeof zonesError === 'string'
          ? zonesError
          : (zonesError && typeof (zonesError as any).message === 'string' ? (zonesError as any).message : '');
        throw new Error(farmErrorMsg || zonesErrorMsg || 'Failed to load data');
      }
      
      setFarm(farmData || null);
      setZones(zonesData || []);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load farm and zones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!farmId) return;

    try {
      const trimmedName = formData.name.trim();
      if (!trimmedName) {
        setSnackbar({ open: true, message: 'Zone name is required', severity: 'error' });
        return;
      }

      if (editingZone) {
        const { data: updatedZone, error: updateError } = await api.zones.update(editingZone.id, trimmedName);
        if (updateError) {
          throw new Error((updateError as any)?.message || 'Failed to update zone');
        }
        setSnackbar({ open: true, message: 'Zone updated successfully', severity: 'success' });
      } else {
        const { data: newZone, error: createError } = await api.zones.create(trimmedName, farmId);
        if (createError) {
          throw new Error((createError as any)?.message || 'Failed to create zone');
        }
        setZones(prev => [...prev, newZone[0]]); // newZone is an array
        setSnackbar({ open: true, message: 'Zone created successfully', severity: 'success' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save zone', severity: 'error' });
      setSnackbar({ open: true, message: error instanceof Error ? error.message : 'Failed to save zone', severity: 'error' });
    } finally {
      handleCloseDialog();
      fetchFarmAndZones();
    }
  };

  const handleDelete = () => {
    if (confirmDelete && farmId) {
      api.zones.delete(confirmDelete.id)
        .then(({ error: deleteError }) => {
          if (deleteError) {
            throw new Error((deleteError && typeof (deleteError as any).message === 'string') ? (deleteError as any).message : 'Failed to delete zone');
          }
          setSnackbar({
            open: true,
            message: 'Zone deleted successfully',
            severity: 'success'
          });
          setConfirmDelete(null);
          fetchFarmAndZones();
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'Failed to delete zone',
            severity: 'error'
          });
        });
    }
  };

  const handleOpenDialog = (zone?: Zone) => {
    setOpenDialog(true);
    setEditingZone(zone ?? null);
    if (zone) {
      setFormData({ name: zone.name });
    } else {
      setFormData({ name: '' });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingZone(null);
    setFormData({ name: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSnackbarClose = () => {
    setSnackbar(s => ({ ...s, open: false }));
  };

  useEffect(() => {
    if (farmId) {
      fetchFarmAndZones();
    }
  }, [farmId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!farm) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Farm not found.
      </Alert>
    );
  }

  const filteredZones = zones.filter(zone =>
    zone.name.toLowerCase().includes(formData.name.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/farms')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Farms
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate(`/farms/${farmId}`)}
        >
          {farm?.name}
        </Link>
        <Typography color="text.primary">Zones</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          {farm?.name} Zones
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Zone
        </Button>
      </Box>

      <Grid container spacing={3}>
        {filteredZones.map((zone: Zone) => (
          <Grid item xs={12} sm={6} md={4} key={zone.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2">
                  {zone.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(zone.created_at).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => handleOpenDialog(zone)}
                >
                  <Edit sx={{ mr: 1 }} />
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setConfirmDelete(zone)}
                >
                  <Delete sx={{ mr: 1 }} />
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingZone ? 'Edit Zone' : 'Add Zone'}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Zone Name"
              type="text"
              fullWidth
              variant="standard"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingZone ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this zone?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ZoneList;
